import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Percent } from "lucide-react";

const RULE_TYPES = ["product", "category", "customer", "lane", "account", "min_density", "exception"];
const empty = { rule_type: "category", match_value: "", fak_class: "", reason: "", active: true };

export default function FakRules() {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/fak-rules").then((r) => setRules(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/fak-rules/${editing.id}`, form);
      else await api.post("/fak-rules", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => { if (!window.confirm("Delete FAK rule?")) return; try { await api.delete(`/fak-rules/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">FedEx FAK Rules</h1>
          <p className="mt-1 text-sm text-muted-foreground">Preserve the calculated class while rating under your FedEx FAK contract</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-fak-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Plus className="mr-1.5 h-4 w-4" />Add Rule</Button>
      </div>

      <div className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">How FAK works here:</span> the density-based NMFC class is always preserved.
        When a rule matches, FedEx Freight is rated under the configured FAK class instead. Both values are shown in diagnostics and on every quote.
      </div>

      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Rule Type</TableHead>
              <TableHead>Match Value</TableHead>
              <TableHead>FAK Class</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Percent className="mx-auto mb-2 h-7 w-7 opacity-40" />No FAK rules.</TableCell></TableRow>}
            {rules.map((r) => (
              <TableRow key={r.id} className="border-border hover:bg-secondary/40" data-testid={`fak-row-${r.id}`}>
                <TableCell className="text-sm font-semibold capitalize">{r.rule_type}</TableCell>
                <TableCell className="text-sm">{r.match_value || "—"}</TableCell>
                <TableCell><span className="font-semibold text-[hsl(var(--primary))]">Class {r.fak_class}</span></TableCell>
                <TableCell className="max-w-xs text-xs text-muted-foreground">{r.reason}</TableCell>
                <TableCell><span className={`inline-block h-2 w-2 rounded-full ${r.active ? "bg-[hsl(var(--success))]" : "bg-zinc-500"}`} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(r); setForm(r); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="fak-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit FAK Rule" : "Add FAK Rule"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Rule Type</Label>
                <Select value={form.rule_type} onValueChange={(v) => setForm({ ...form, rule_type: v })}>
                  <SelectTrigger className="mt-1.5 h-10" data-testid="fak-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{RULE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-caps">FAK Class</Label><Input value={form.fak_class} onChange={(e) => setForm({ ...form, fak_class: e.target.value })} className="mt-1.5 h-10" data-testid="fak-class" /></div>
            </div>
            <div><Label className="label-caps">Match Value (product name, category, customer type…)</Label><Input value={form.match_value} onChange={(e) => setForm({ ...form, match_value: e.target.value })} className="mt-1.5 h-10" data-testid="fak-match" placeholder="e.g. Plastic Pallet" /></div>
            <div><Label className="label-caps">Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 h-10" data-testid="fak-reason" /></div>
            <label className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></label>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-fak-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
